import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

const SERVICE_OPTIONS = [
  ["chc", "Personal Assistance Services (CHC)"], ["veterans", "Home Care Assistance (Veteran's Benefit)"],
  ["personal-care", "Personal Care (Private)"], ["ltc", "Long Term Care Services (LTC Insurance)"],
  ["companionship", "Companionship (Private)"], ["housekeeping", "Housekeeping (Private)"], ["transportation", "Transportation (Private)"],
];

const Field = ({ label, value, onChange, type = "text", testid, placeholder = "" }) => (
  <div>
    <Label className="text-[11px] text-stone-500">{label}</Label>
    <Input type={type} value={value || ""} onChange={onChange} placeholder={placeholder}
      className="mt-1 h-9 border-stone-300 bg-white text-sm" data-testid={testid} />
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <Label className="text-[11px] text-stone-500">{label}</Label>
    <select value={value || ""} onChange={onChange} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid={testid}>
      <option value="">—</option>
      {options.map((option) => <option key={option}>{option}</option>)}
    </select>
  </div>
);

export default function ServiceAgreementEditorPage() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const [agr, setAgr] = useState(null);
  const [template, setTemplate] = useState(null);
  const [busy, setBusy] = useState("");
  const [signForm, setSignForm] = useState({});

  const sign = async () => {
    setBusy("sign");
    try {
      await api.post(`/clients/${id}/agreement/sign`, signForm);
      toast.success("Agreement signed — the signed PDF is saved to the client's paperwork");
      const { data } = await api.get(`/clients/${id}/agreement`);
      setAgr(data.agreement);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const copySigningLink = async () => {
    setBusy("link");
    try {
      const { data } = await api.post(`/clients/${id}/intake-links`, {});
      const url = `${window.location.origin}/intake/${data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Signing link copied — text or email it to the client (good for 7 days)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    if (loading || user?.role !== "admin") return;
    Promise.all([
      api.get(`/clients/${id}/agreement`),
      api.get("/forms/service-agreement-template"),
    ]).then(([agreementRes, templateRes]) => {
      const agreement = agreementRes.data.agreement;
      setAgr({
        ...agreement,
        reviewed_by: agreement.reviewed_by || user.name || user.email,
        reviewed_at: agreement.reviewed_at || new Date().toISOString().slice(0, 10),
      });
      setSignForm({
        ...(agreement.initials || {}),
        signer_name: agreement.signed?.signer_name || agreement.client_name || "",
        relationship: agreement.signed?.relationship || "",
        agency_signer: agreement.signed?.agency_signer || user.name || user.email,
      });
      setTemplate(templateRes.data);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [id, user, loading]);

  if (loading) return <div className="py-16 text-center text-sm text-stone-400" data-testid="service-agreement-loading">Loading agreement…</div>;
  if (user?.role !== "admin") return <div className="rounded-xl border border-stone-200 bg-white p-8 text-sm text-stone-600" data-testid="service-agreement-no-access">Only office administrators can edit service agreements.</div>;
  if (!agr || !template) return <div className="py-16 text-center text-sm text-stone-400" data-testid="service-agreement-loading">Loading agreement…</div>;

  const set = (key) => (e) => setAgr({ ...agr, [key]: e.target.value });
  const toggleService = (service) => {
    const current = new Set(agr.services || []);
    if (current.has(service)) current.delete(service); else current.add(service);
    setAgr({ ...agr, services: Array.from(current) });
  };
  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/clients/${id}/agreement`, agr);
      setAgr(data.agreement);
      if (data.signature_reset) toast.warning("Agreement changed after signing — a new signature is required.");
      else toast.success("Service agreement form saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const openPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/clients/${id}/agreement/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const selectedServices = SERVICE_OPTIONS.map(([, service]) => `${(agr.services || []).includes(service) ? "[x]" : "[ ]"} ${service}`).join("\n");
  const shiftLines = [1, 2, 3].map((n) => {
    const days = agr[`shift${n}_days`] || "";
    const time = agr[`shift${n}_time`] || "";
    const caregiver = agr[`shift${n}_caregiver`] || "";
    return days || time || caregiver ? `Shift ${n} — ${[days && `Days: ${days}`, time && `Time: ${time}`, caregiver && `Caregiver: ${caregiver}`].filter(Boolean).join(" | ")}` : "";
  }).filter(Boolean).join("\n");
  const bodyText = (body) => String(body || "")
    .replace("{services_text}", selectedServices)
    .replace("{shift_lines}", shiftLines || "Shift days, times, and caregiver assignments are listed in the Care Plan.")
    .replace("{services_notes}", agr.services_notes || "")
    .replace("{start_date}", agr.start_date || "__________")
    .replace("{rate_amount}", agr.rate_amount || "__________")
    .replace("{rate_unit}", agr.rate_unit || "__________")
    .replace("{payment_method}", agr.payment_method || "__________")
    .replace("{autopay}", agr.autopay || "__________");

  const serviceBlock = (
    <div className="my-4 rounded-lg border border-stone-200 bg-stone-50 p-4" data-testid="service-agreement-service-options">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {SERVICE_OPTIONS.map(([key, service]) => (
          <label key={key} className="flex items-center gap-2 text-sm text-stone-700">
            <input type="checkbox" checked={(agr.services || []).includes(service)} onChange={() => toggleService(service)} data-testid={`service-agreement-service-${key}`} />
            {service}
          </label>
        ))}
      </div>
      <div className="mt-4">
        <Label className="text-[11px] text-stone-500">Service notes / proposed schedule source</Label>
        <textarea value={agr.services_notes || ""} onChange={set("services_notes")} rows={3}
          className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="service-agreement-services-notes" />
      </div>
    </div>
  );

  const scheduleBlock = (
    <div className="my-4 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="service-agreement-schedule-fields">
      {[1, 2, 3].map((n) => (
        <div key={n} className="rounded-lg border border-stone-200 bg-stone-50 p-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-stone-400">Shift {n}</p>
          <div className="space-y-3">
            <Field label="Days" value={agr[`shift${n}_days`]} onChange={set(`shift${n}_days`)} testid={`service-agreement-shift${n}-days`} />
            <Field label="Time" value={agr[`shift${n}_time`]} onChange={set(`shift${n}_time`)} testid={`service-agreement-shift${n}-time`} />
            <Field label="Assigned caregiver (if known)" value={agr[`shift${n}_caregiver`]} onChange={set(`shift${n}_caregiver`)} testid={`service-agreement-shift${n}-caregiver`} />
          </div>
        </div>
      ))}
    </div>
  );

  const paymentBlock = (
    <div className="my-4 grid grid-cols-1 gap-4 rounded-lg border border-stone-200 bg-stone-50 p-4 md:grid-cols-2" data-testid="service-agreement-payment-fields">
      <Field label="Rate for service ($)" value={agr.rate_amount} onChange={set("rate_amount")} testid="service-agreement-rate-amount" />
      <SelectField label="Rate unit" value={agr.rate_unit} onChange={set("rate_unit")} options={["Per Visit", "Hourly", "Weekly"]} testid="service-agreement-rate-unit" />
      <SelectField label="Payment method" value={agr.payment_method} onChange={set("payment_method")} options={["Check by mail", "Cash App", "Venmo", "Credit card by phone", "CHC / Medicaid — not billed to client"]} testid="service-agreement-payment-method" />
      <SelectField label="Autopay enrollment" value={agr.autopay} onChange={set("autopay")} options={["Yes", "No"]} testid="service-agreement-autopay" />
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl pb-24" data-testid="service-agreement-editor">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to={`/clients/${id}`} className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-stone-500 hover:text-primary" data-testid="service-agreement-back">
            <ArrowLeft size={14} /> Back to client
          </Link>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Service Agreement Form</h1>
          <p className="mt-1 text-sm text-stone-500">Complete the prefilled form below, review it, then open the PDF for signature.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openPdf} disabled={busy === "pdf"} className="rounded-full border-stone-300" data-testid="service-agreement-open-pdf">
            <Printer size={15} className="mr-1.5" /> {busy === "pdf" ? "Opening…" : "Open PDF"}
          </Button>
          <Button onClick={save} disabled={busy === "save"} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="service-agreement-save">
            {busy === "save" ? "Saving…" : "Save form"}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="service-agreement-document">
        <div className="border-b border-stone-200 bg-stone-50 px-8 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="service-agreement-review-stamp">
            <Field label="Reviewed by" value={agr.reviewed_by} onChange={set("reviewed_by")} testid="service-agreement-reviewed-by" />
            <Field label="Reviewed on" type="date" value={agr.reviewed_at} onChange={set("reviewed_at")} testid="service-agreement-reviewed-at" />
            <div className="flex items-end text-xs text-stone-500">Office review — shown on the agreement tracking list.</div>
          </div>
        </div>

        <div className="px-8 py-8 font-serif text-[15px] leading-relaxed text-stone-800">
          <div className="text-center">
            <p className="text-2xl font-bold tracking-tight text-[#4A235A]">Silver Home Care</p>
            <p className="text-lg font-semibold">Service Agreement and Authorization for Care</p>
            <p className="mt-1 text-sm text-stone-500">Non-Medical Home Care</p>
          </div>

          <section className="mt-8" data-testid="agreement-section-terms">
            <h2 className="text-lg font-bold">Terms of Home Care Service Agreement</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 font-sans md:grid-cols-2">
              <Field label="Effective date" type="date" value={agr.effective_date} onChange={set("effective_date")} testid="service-agreement-effective-date" />
              <Field label="Service start date" type="date" value={agr.start_date} onChange={set("start_date")} testid="service-agreement-start-date" />
            </div>
            <p className="mt-4">This agreement will continue on an as-needed basis until terminated by either party.</p>
          </section>

          <section className="mt-8" data-testid="agreement-section-client">
            <h2 className="text-lg font-bold">Between the Agency and the Client</h2>
            <p className="mt-3"><strong>Agency:</strong> Silver Home Care · 680 Lincoln Hwy, Fairless Hills, PA 19030 · 800-719-6912 · Support@silverhomecare.com</p>
            <p className="text-xs text-stone-500">Mailing address (payments only, since Sept 2024): 12 Penns Trail, Newtown, PA 18940</p>
            <div className="mt-4 grid grid-cols-1 gap-4 font-sans md:grid-cols-2">
              <Field label="Client’s full name" value={agr.client_name} onChange={set("client_name")} testid="service-agreement-client-name" />
              <Field label="Date of birth" type="date" value={agr.dob} onChange={set("dob")} testid="service-agreement-dob" />
              <Field label="Full address" value={agr.client_address} onChange={set("client_address")} testid="service-agreement-client-address" />
              <Field label="Medicaid / member ID (if any)" value={agr.medicaid_id} onChange={set("medicaid_id")} testid="service-agreement-medicaid-id" />
              <Field label="Authorized person for care decisions" value={agr.authorized_person} onChange={set("authorized_person")} testid="service-agreement-authorized-person" />
              <Field label="Authorized person type" value={agr.authorized_type} onChange={set("authorized_type")} testid="service-agreement-authorized-type" placeholder="Leave blank if the client decides for themselves" />
              <Field label="Emergency contact (name / relationship)" value={agr.emergency_name} onChange={set("emergency_name")} testid="service-agreement-emergency-name" />
              <Field label="Emergency contact phone" value={agr.emergency_phone} onChange={set("emergency_phone")} testid="service-agreement-emergency-phone" />
              <Field label="Client email" value={agr.email} onChange={set("email")} testid="service-agreement-client-email" />
            </div>
          </section>

          {template.sections.map((section) => (
            <section key={section.heading} className="mt-8" data-testid={`agreement-text-${section.heading.split(".")[0].replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <h2 className="text-lg font-bold">{section.heading}</h2>
              <p className="mt-2 whitespace-pre-line">{bodyText(section.body)}</p>
              {section.heading.startsWith("1.") && serviceBlock}
              {section.heading.startsWith("4.") && scheduleBlock}
              {section.heading.startsWith("5.") && paymentBlock}
            </section>
          ))}

          <section className="mt-8" data-testid="agreement-section-signatures">
            <h2 className="text-lg font-bold">Authorizations, Initials, and Signatures</h2>
            {agr.signed?.signed_at ? (
              <div className="mt-3 rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-4" data-testid="agreement-signed-banner">
                <p className="text-sm font-semibold text-[#1e8449]">
                  Signed by {agr.signed.signer_name} on {String(agr.signed.signed_at).slice(0, 10)}
                  {agr.signed.relationship ? ` (${agr.signed.relationship})` : ""} · agency: {agr.signed.agency_signer || "—"}
                </p>
                <p className="mt-1 text-xs text-stone-500">The signed PDF is frozen in the client's paperwork. Changing any term above resets the signature and requires a new sign-off.</p>
              </div>
            ) : (
              <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-4" data-testid="agreement-sign-card">
                <p className="text-xs text-stone-500">Complete the initials and typed signatures, then sign off. The signed PDF is frozen to the client's record; editing any term later resets it.</p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                  {(template.initial_sections || []).map((item) => (
                    <div key={item.key} className="rounded-lg border border-stone-200 bg-white p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">{item.title}</p>
                      <p className="mt-1 text-[11px] leading-snug text-stone-500">{item.body}</p>
                      <Input value={signForm[item.key] || ""} maxLength={4}
                        onChange={(e) => setSignForm({ ...signForm, [item.key]: e.target.value })}
                        placeholder="Initials" className="mt-2 h-8 w-20 border-stone-300 text-sm uppercase"
                        data-testid={`agreement-initials-${item.key}`} />
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 font-sans md:grid-cols-2">
                  <Field label="Client / authorized representative — type full legal name (this is the signature)" value={signForm.signer_name} onChange={(e) => setSignForm({ ...signForm, signer_name: e.target.value })} testid="agreement-signer-name" />
                  <Field label="Relationship to client (if a representative signs)" value={signForm.relationship} onChange={(e) => setSignForm({ ...signForm, relationship: e.target.value })} testid="agreement-signer-relationship" placeholder="Self / daughter / POA…" />
                  <Field label="Agency representative signature (typed name)" value={signForm.agency_signer} onChange={(e) => setSignForm({ ...signForm, agency_signer: e.target.value })} testid="agreement-agency-signer" />
                </div>
                <Button onClick={sign} disabled={busy === "sign"}
                  className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-stone-900"
                  data-testid="agreement-sign-button">
                  {busy === "sign" ? "Signing…" : "Sign off & freeze signed PDF"}
                </Button>
                <div className="mt-4 border-t border-stone-200 pt-3">
                  <p className="text-xs font-semibold text-stone-600">They sign it themselves instead?</p>
                  <p className="mt-0.5 text-xs text-stone-500">
                    Send them the signing link — they read the agreement on their phone and draw their own signature
                    (that version carries their drawn signature, initials, date, and device record).
                  </p>
                  <Button variant="outline" onClick={copySigningLink} disabled={busy === "link"}
                    className="mt-2 rounded-full" data-testid="agreement-signing-link">
                    {busy === "link" ? "Making link…" : "Copy client signing link"}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
